import * as React from "react";
import { Link, Text, makeStyles, Spinner, MessageBarIntent } from "@fluentui/react-components";
import WelcomeScreen from './components/WelcomeScreen';
import LoginScreen from './components/LoginScreen';
import ForgotPasswordScreen from './components/ForgotPasswordScreen';
import ChatInterface, { Message } from './components/ChatInterface';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const REGISTER_URL = process.env.REACT_APP_API_REGISTER_URL_DEV;
const LOCAL_STORAGE_TOKEN_KEY = 'authToken';

const useAppStyles = makeStyles({
  taskpaneContainer: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    boxSizing: "border-box",
  },
  mainContent: {
    flexGrow: 1,
    overflowY: "auto",
    position: "relative",
    display: 'flex',
    flexDirection: 'column',
  },
  footer: {
    flexShrink: 0,
    textAlign: "center",
    padding: "10px",
    borderTopWidth: "1px",
    borderTopStyle: "solid",
    borderTopColor: "#e0e0e0",
    backgroundColor: "#f5f5f5",
  },
  footerLink: {
    fontSize: "12px",
    marginLeft: "8px",
    marginRight: "8px",
  }
});

interface AppProps {
  title: string;
}
interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
}
interface Model {
  id: number;
  name: string;
}
export interface ChatSummary {
  id: number;
  name: string;
  active?: boolean;
  deleted?: boolean | null;
}

type AppMessage = { text: string; type: MessageBarIntent } | null;

const App: React.FC<AppProps> = ({  }) => {
  const styles = useAppStyles();

  const [view, setView] = React.useState<'welcome' | 'login' | 'forgotPassword' | 'chat'>('welcome');
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [isLoadingChat, setIsLoadingChat] = React.useState<boolean>(false);
  const [message, setMessage] = React.useState<AppMessage>(null);
  const [authToken, setAuthToken] = React.useState<string | null>(null);
  const [user, setUser] = React.useState<User | null>(null);
  const [messages, setMessages] = React.useState<Message[]>([]);
  const [models, setModels] = React.useState<Model[]>([]);
  const [chatsList, setChatsList] = React.useState<ChatSummary[]>([]);
  const [currentChatId, setCurrentChatId] = React.useState<number | null>(null);
  const isCreatingChatRef = React.useRef(false);
  let authDialog: Office.Dialog | undefined;

  const clearStatus = () => { setMessage(null); setIsLoading(false); setIsLoadingChat(false); };
  const goToLogin = () => { clearStatus(); setView('login'); };
  const goToForgotPassword = () => { clearStatus(); setView('forgotPassword'); };
  const goToChat = (chatIdToLoad?: number | null) => {
    clearStatus();
    setMessages([]);
    console.log(`Navegando a chat. ID a cargar: ${chatIdToLoad}`);
    setCurrentChatId(chatIdToLoad ?? null);
    setView('chat');
   };
  const openMoreInfo = () => window.open('https://lexfania.ai/', '_blank');
  const openRegisterLink = () => window.open(REGISTER_URL, '_blank');

  const handleLogout = () => {
    console.log("Cerrando sesión...");
    setAuthToken(null);
    setUser(null);
    setMessages([]);
    setModels([]);
    setChatsList([]);
    setCurrentChatId(null);
    localStorage.removeItem(LOCAL_STORAGE_TOKEN_KEY);
    goToLogin();
  };

  const fetchApi = async (url: string, options: RequestInit = {}, tokenOverride?: string | null): Promise<any> => {
    const currentToken = tokenOverride !== undefined ? tokenOverride : authToken;
    if (!currentToken && url !== `${API_BASE_URL}/auth` && !url.includes('/users/forgot-password')) {
      throw new Error("Usuario no autenticado.");
    }
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(currentToken && { 'Authorization': `Bearer ${currentToken}` }),
      ...options.headers,
    };
    const response = await fetch(url, { ...options, headers: headers });
    if (options.headers && (options.headers as any)['Accept'] === 'text/event-stream') {
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Error en ${options.method || 'GET'} ${url}:`, errorText);
        let errorMsg = `Error HTTP ${response.status}`;
        try { const errorJson = JSON.parse(errorText); errorMsg = errorJson?.error?.message || errorJson?.error || errorJson?.message || errorMsg; } catch (e) {}
        throw new Error(errorMsg);
      }
      return response;
    }
    const responseBodyText = await response.text();
    if (response.status === 204) return null;
    let responseData: any = null;
    let parseError: Error | null = null;
    if (responseBodyText) {
      try { responseData = JSON.parse(responseBodyText); } catch (e) { parseError = e as Error; console.warn(`Failed JSON parse ${url}:`, responseBodyText); }
    }
    if (!response.ok) {
      const errorMsg = responseData?.error?.message || responseData?.error || responseData?.message || (parseError ? `Respuesta inválida (Status: ${response.status})` : `Error HTTP ${response.status}`);
      console.error(`Error en ${options.method || 'GET'} ${url}:`, responseData || responseBodyText);
      if (responseData?.error?.message?.chatId) throw new Error(responseData.error.message.chatId);
      throw new Error(errorMsg);
    }
    if (parseError && responseData === null) throw new Error(`Respuesta exitosa pero inesperada (no JSON) (Status: ${response.status})`);
    return responseData;
  };

  const fetchUserData = async (token: string): Promise<User | null> => {
    if (!token) return null;
    try {
      const userData = await fetchApi(`${API_BASE_URL}/users`, {}, token);
      if (!userData) return null;
      console.log("Datos del usuario obtenidos:", userData);
      setUser(userData as User);
      return userData as User;
    } catch (userFetchError: any) {
      console.error('Error en fetch de usuario:', userFetchError);
      setMessage({ text: `Error al cargar perfil: ${userFetchError.message}`, type: "error" });
      setAuthToken(null); setUser(null); localStorage.removeItem(LOCAL_STORAGE_TOKEN_KEY);
      return null;
    }
  };

  const fetchInitialData = async (token: string) => {
    if (!token) return { models: [], initialChatId: null };
    try {
      const data = await fetchApi(`${API_BASE_URL}/chats/names`, {}, token);
      let fetchedModels: Model[] = [];
      let initialChatId: number | null = null;
      let activeChats: ChatSummary[] = [];
      if (data && data.models && Array.isArray(data.models)) {
        console.log("Modelos obtenidos:", data.models);
        fetchedModels = data.models;
        setModels(fetchedModels);
      } else {
        console.warn("No se encontraron modelos en la respuesta de /chats");
        setModels([]);
      }

      if (data && data.chats && Array.isArray(data.chats)) {
        console.log("Chats obtenidos:", data.chats);
        activeChats = data.chats;
        console.log("Chats activos:", activeChats);

        const sortedChats = activeChats;

        setChatsList(sortedChats);

        if (sortedChats.length > 0) {
          initialChatId = sortedChats[0].id;
          console.log(`Seleccionando chat inicial activo con ID: ${initialChatId}`);
          setCurrentChatId(initialChatId);
        } else {
          console.log("El usuario no tiene chats activos existentes.");
          setCurrentChatId(null);
        }
      } else {
        console.warn("No se encontraron chats en la respuesta de /chats");
        setChatsList([]);
        setCurrentChatId(null);
      }
      return { models: fetchedModels, initialChatId };
    } catch (error: any) {
      console.error("Error fetching initial data (chats/models):", error);
      setMessage({ text: `Error al cargar datos iniciales: ${error.message}`, type: "error" });
      setModels([]);
      setChatsList([]);
      setCurrentChatId(null);
      return { models: [], initialChatId: null };
    }
  };

  const fetchChatHistory = async (chatId: number) => {
    if (!authToken) return;
    console.log(`Cargando historial para chat ID: ${chatId}`);
    setIsLoadingChat(true);
    setMessage(null);
    try {
      const historyData = await fetchApi(`${API_BASE_URL}/chats/${chatId}`);

      if (historyData && historyData.chat && historyData.chat.ChatQuestions && Array.isArray(historyData.chat.ChatQuestions)) {
          const formattedMessages: Message[] = historyData.chat.ChatQuestions.flatMap((q: any) => [
            { id: `q-${q.id}`, sender: 'user', text: q.content },
            ...(q.ChatAnswers?.map((a: any) => ({
              id: `a-${a.id}`,
              sender: 'bot',
              text: a.content
            })) || [])
          ]);
          setMessages(formattedMessages);
      } else {
        console.warn("Historial de chat vacío o formato inesperado:", historyData);
        setMessages([]);
      }
    } catch (error: any) {
      console.error(`Error fetching history for chat ${chatId}:`, error);
      setMessage({ text: `Error al cargar historial: ${error.message}`, type: "error" });
      setMessages([]);
    } finally {
      setIsLoadingChat(false);
    }
  };

  React.useEffect(() => {
    const checkStoredToken = async () => {
      const storedToken = localStorage.getItem(LOCAL_STORAGE_TOKEN_KEY);
      if (storedToken) {
        console.log("Token encontrado, validando...");
        setAuthToken(storedToken);
        const fetchedUser = await fetchUserData(storedToken);
        if (fetchedUser) {
          const { initialChatId } = await fetchInitialData(storedToken);
          if (initialChatId) {
            await fetchChatHistory(initialChatId);
          }

          goToChat(initialChatId);
        } else {
          goToLogin();
        }
      } else {
        console.log("No se encontró token.");
        setIsLoading(false);
      }
    };
    checkStoredToken();
  }, []);

  React.useEffect(() => {
    if (view === 'chat' && currentChatId !== null) {
  // If a chat is being created, do not clear messages
      if (!isCreatingChatRef.current) {
        fetchChatHistory(currentChatId);
      }
    } else if (view === 'chat' && currentChatId === null) {
      setMessages([]);
    }
    if (isCreatingChatRef.current) {
      isCreatingChatRef.current = false;
    }
  }, [currentChatId, view]);


  const handleEmailLogin = async (email: string, password: string) => {
    const url = `${API_BASE_URL}/auth`;
    const loginData = { email, password };
    setMessage(null);
    setIsLoading(true);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData),
      });
      const responseData = await response.json();

      if (!response.ok || responseData.error) {
        const errorData = responseData.error || { message: `HTTP error! status: ${response.status}`, code: 'unknown' };
        console.error('Login API Error:', errorData);
        let errorMessage = "Credenciales incorrectas o error desconocido.";
        if (typeof errorData.message === 'string') errorMessage = errorData.message;
        setMessage({ text: errorMessage, type: "error" });
        setIsLoading(false);
        throw new Error(errorMessage);
      }

      const jwtToken = responseData.token;
      localStorage.setItem(LOCAL_STORAGE_TOKEN_KEY, jwtToken);
      setAuthToken(jwtToken);
      const fetchedUser = await fetchUserData(jwtToken);

      if (fetchedUser) {
        const { initialChatId } = await fetchInitialData(jwtToken);
        if (initialChatId) await fetchChatHistory(initialChatId);
        goToChat(initialChatId || undefined);
      } else {
        setIsLoading(false);
        throw new Error("Error al cargar el perfil del usuario.");
      }

    } catch (networkError) {
      console.error('Error de red o fetch en login:', networkError);
      setMessage({ text: 'Error de conexión. Por favor, verifica tu internet.', type: "error" });
      setIsLoading(false);
      throw networkError;
    }
  };

  type DialogEventArgs = { message: string; origin: string; } | { error: number; };
  const processMessageFromDialog = async (arg: DialogEventArgs ) => {
    if ('message' in arg) {
        let receivedToken = arg.message;

        let parseToken: { token?: string} | null = null;

        try {
          parseToken = JSON.parse(receivedToken);
        } catch (e) {
          setMessage({ text: "Error: respuesta inválida del proceso de autenticación.", type: "error" });
          setIsLoading(false);
          if (authDialog) { try { authDialog.close(); } catch (e) { /* Ignore */ } }
          return;
        }

        receivedToken = parseToken?.token;

        if (receivedToken && typeof receivedToken === 'string') {
            localStorage.setItem(LOCAL_STORAGE_TOKEN_KEY, receivedToken);
            setAuthToken(receivedToken);
            const fetchedUser = await fetchUserData(receivedToken);
            if (fetchedUser) {
                const { initialChatId } = await fetchInitialData(receivedToken);
                if (initialChatId) await fetchChatHistory(initialChatId);
                goToChat(initialChatId || undefined);
            } else { setIsLoading(false); }
        } else {
            setMessage({ text: "Se recibió una respuesta inválida del proceso de autenticación.", type: "error" });
            setIsLoading(false);
        }
        if (authDialog) { try { authDialog.close(); } catch (e) { /* Ignore */ } }
    }
}

const processDialogEvent = (arg: DialogEventArgs) => {
    if ('error' in arg) {
        console.error(`Diálogo cerrado con error/código: ${arg.error}`);
        if (arg.error !== 12006) {
            setMessage({ text: "Ocurrió un error durante el proceso de autenticación externa.", type: "error" });
        }
    }
    authDialog.close();
    setIsLoading(false);
};


  const startOAuthFlow = (provider: 'google' | 'linkedin' ) => {
    setIsLoading(true);
    setMessage(null);

    const dialogOptions = { height: 70, width: 50, displayInIframe: false };
    const ApiBaseUrl = encodeURIComponent(API_BASE_URL || '');
    const dialogLoginUrl: string = location.protocol + '//' + location.hostname + (location.port ? ':' + location.port : '') + `/assets/login.html?provider=${provider}&apiBaseUrl=${ApiBaseUrl}`;

    Office.context.ui.displayDialogAsync(dialogLoginUrl, dialogOptions, (result) => {
      console.log(result);
      if (result.status === Office.AsyncResultStatus.Failed) {
        console.error(`Error al abrir diálogo ${provider}:`, result.error.message);
        setMessage({ text: `No se pudo iniciar el proceso con ${provider}.`, type: "error" });
        setIsLoading(false);
      } else {
        authDialog = result.value;
        authDialog.addEventHandler(Office.EventType.DialogMessageReceived, processMessageFromDialog);
        authDialog.addEventHandler(Office.EventType.DialogEventReceived, processDialogEvent);
      }
    });
  }

  const handleGoogleLogin = () => startOAuthFlow('google');
  const handleLinkedInLogin = () => startOAuthFlow('linkedin');

  const handleForgotPasswordSubmit = async (email: string) => {
    console.log("Enviando recuperación para:", email);
    setMessage(null); setIsLoading(true);
    const url = `${API_BASE_URL}/users/forgot-password`;
    try {
      const responseData = await fetchApi(url, {
        method: 'POST',
        body: JSON.stringify({ email }),
      }, null);
      setMessage({ text: responseData.message || "Instrucciones enviadas.", type: "success" });
    } catch (err: any) {
      console.error("Error forgot password:", err);
      setMessage({ text: err.message || "Error al enviar la solicitud.", type: "error" });
    } finally {
      setIsLoading(false);
    }
  };


  const handleSendMessage = async (messageText: string) => {
    console.log("Enviando mensaje:", messageText);
    const userMessage: Message = { id: `user-${Date.now()}`, sender: "user", text: messageText };
    setMessages((prevMessages) => [...prevMessages, userMessage]);
    setIsLoadingChat(true); setMessage(null);

    let chatIdToUse = currentChatId;
    let temporaryBotMessageId = `bot-${Date.now()}`;

    try {
      if (!authToken) throw new Error("Usuario no autenticado.");
      const selectedModelId = models.length > 0 ? models[0].id : null;
      if (!selectedModelId) throw new Error("No hay modelos de IA disponibles.");

      if (chatIdToUse === null) {
        console.log("Creando nuevo chat...");
        isCreatingChatRef.current = true;
        const newChatData = await fetchApi(`${API_BASE_URL}/chats/placeholder`, {
          method: 'POST',
          body: JSON.stringify({ name: messageText.substring(0, 30) })
        });
        if (newChatData && newChatData.id) {
            chatIdToUse = newChatData.id;
            setCurrentChatId(chatIdToUse);
            setChatsList(prev => [newChatData, ...prev]);
            console.log(`Nuevo chat creado con ID: ${chatIdToUse}`);
        } else { throw new Error("No se pudo crear un nuevo chat."); }
      }

      if (chatIdToUse === null) throw new Error("ID de chat inválido.");

      const url = `${API_BASE_URL}/chats/chatQuestion?streamProtocol=progress-v1`;
      const body = { content: messageText, chatId: chatIdToUse, modelId: selectedModelId, init: true };

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
          'Accept': 'text/event-stream'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMsg = `Error HTTP ${response.status}`;
        try { const errorJson = JSON.parse(errorText); errorMsg = errorJson?.error?.message || errorJson?.error || errorJson?.message || errorMsg; } catch (e) {}
        throw new Error(errorMsg);
      }
      if (!response.body) throw new Error("La respuesta no contenía un cuerpo.");

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let done = false;
      let streamedText = "";
      let streamProtocol: "legacy" | "progress-v1" | null = null;
      let protocolDetectionBuffer = "";
      let sseBuffer = "";

      setMessages((prevMessages) => [...prevMessages, { id: temporaryBotMessageId, sender: "bot", text: "" }]);

      const appendBotText = (text: string) => {
        if (!text) return;
        streamedText += text;
        setMessages((prevMessages) =>
          prevMessages.map((msg) =>
            msg.id === temporaryBotMessageId ? { ...msg, text: streamedText } : msg
          )
        );
      };

      const processSseFrame = (frame: string) => {
        let eventName = "message";
        const dataLines: string[] = [];

        for (const line of frame.split(/\r?\n/)) {
          if (line.startsWith("event:")) eventName = line.slice(6).trim();
          if (line.startsWith("data:")) dataLines.push(line.slice(5).replace(/^ /, ""));
        }

        if (eventName === "error" && dataLines.length > 0) {
          let errorMessage = "Error en la generación de respuesta.";
          try {
            const payload: unknown = JSON.parse(dataLines.join("\n"));
            if (
              payload !== null &&
              typeof payload === "object" &&
              "message" in payload &&
              typeof payload.message === "string"
            ) errorMessage = payload.message;
          } catch {}
          throw new Error(errorMessage);
        }

        if (eventName !== "token" || dataLines.length === 0) return;

        const data = dataLines.join("\n");
        try {
          const payload = JSON.parse(data);
          if (typeof payload?.text === "string") appendBotText(payload.text);
        } catch {
          appendBotText(data);
        }
      };

      const consumeSseFrames = (text: string) => {
        sseBuffer += text;
        let delimiterMatch = sseBuffer.match(/\r?\n\r?\n/);
        while (delimiterMatch && delimiterMatch.index !== undefined) {
          const frame = sseBuffer.slice(0, delimiterMatch.index);
          sseBuffer = sseBuffer.slice(delimiterMatch.index + delimiterMatch[0].length);
          processSseFrame(frame);
          delimiterMatch = sseBuffer.match(/\r?\n\r?\n/);
        }
      };

      while (!done) {
        const { value, done: doneReading } = await reader.read();
        done = doneReading;
        const chunk = decoder.decode(value, { stream: !done });

        if (streamProtocol === "progress-v1") {
          consumeSseFrames(chunk);
        } else if (streamProtocol === "legacy") {
          appendBotText(chunk);
        } else {
          protocolDetectionBuffer += chunk;
          const progressProtocolPrefix = "event: plan\n";
          if (progressProtocolPrefix.startsWith(protocolDetectionBuffer) && protocolDetectionBuffer.length < progressProtocolPrefix.length) {
            continue;
          }
          if (protocolDetectionBuffer.startsWith(progressProtocolPrefix)) {
            streamProtocol = "progress-v1";
            consumeSseFrames(protocolDetectionBuffer);
          } else {
            streamProtocol = "legacy";
            appendBotText(protocolDetectionBuffer);
          }
          protocolDetectionBuffer = "";
        }
      }

      if (streamProtocol === null) appendBotText(protocolDetectionBuffer);
      if (streamProtocol === "progress-v1" && sseBuffer.trim()) processSseFrame(sseBuffer);

      console.log("Streaming finalizado.");

    } catch (err: any) {
      console.error("Error al enviar mensaje:", err);
      const errorMessage = err.message || 'Intenta de nuevo.';
      setMessage({ text: `Error del asistente: ${errorMessage}`, type: "error" });
      const errorBotMessage: Message = {
        id: `error-${Date.now()}`,
        sender: "bot",
        text: `Lo siento, ocurrió un error: ${errorMessage}`,
      };
      setMessages((prevMessages) => {
        const existingBotMsgIndex = prevMessages.findIndex(m => m.id === temporaryBotMessageId);
        if (existingBotMsgIndex > -1) {
          return prevMessages.map((m, i) => i === existingBotMsgIndex ? errorBotMessage : m);
        } else {
          return [...prevMessages, errorBotMessage];
        }
      });
    } finally {
      setIsLoadingChat(false);
      isCreatingChatRef.current = false;
    }
  };

  const handleSelectChat = (chatId: number | null) => {
    if (chatId === currentChatId) return;
    console.log(`Seleccionando chat con ID: ${chatId}`);
    goToChat(chatId);
  };

  const copyAndPaste = async (text: string) => {
    text = text.replace(/<hr\s*\/?>/g, "").replace(/<a\s*[^>]*>(.*?)<\/a>/g, "$1");

    await Word.run(async (context) => {
        const selection = context.document.getSelection();
        const insertedContent = selection.insertHtml(text, Word.InsertLocation.replace);
        const paragraphsInInsertedContent = insertedContent.paragraphs;
        const tablesInInsertedContent = insertedContent.tables.load("items");
        paragraphsInInsertedContent.load("items");
        await context.sync();

        if (tablesInInsertedContent.items.length > 0) {
            const firstTable = tablesInInsertedContent.items[0];
            firstTable.styleBuiltIn = "TableGrid";
        }

        paragraphsInInsertedContent.items.forEach(paragraph => {
            paragraph.alignment = Word.Alignment.justified;
            paragraph.font.name = 'Sans Serif Collection';
        });

        await context.sync();
    });
};

  const renderView = () => {
    if (isLoading && !user && !authToken) {
      return <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%'}}><Spinner label="Cargando..." /></div>;
    }

    switch (view) {
      case 'login':
        return ( <LoginScreen key="login-screen" onEmailLoginSubmit={handleEmailLogin} onGoogleLogin={handleGoogleLogin} onLinkedInLogin={handleLinkedInLogin} onRegisterLinkClick={openRegisterLink} onForgotPassword={goToForgotPassword} isLoading={isLoading} error={message?.type === 'error' ? message.text : null} clearError={() => setMessage(null)} /> );
      case 'forgotPassword':
           return <ForgotPasswordScreen onNavigateBack={goToLogin} onSubmit={handleForgotPasswordSubmit} isLoading={isLoading} message={message} clearMessage={() => setMessage(null)} />;
      case 'chat':
        return authToken && user ? (
          <ChatInterface
            key={currentChatId || 'new-chat'}
            initialMessages={messages}
            onSendMessage={handleSendMessage}
            isLoading={isLoadingChat}
            onLogout={handleLogout}
            chatsList={chatsList}
            currentChatId={currentChatId}
            onSelectChat={handleSelectChat}
            onNewChat={() => handleSelectChat(null)}
            copyAndPaste={copyAndPaste}
          />
         ) : <Spinner label="Cargando sesión..." />;
      case 'welcome':
      default:
        if (authToken) {
          return <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%'}}><Spinner label="Verificando sesión..." /></div>;
        }
        return ( <WelcomeScreen onLoginClick={goToLogin} onMoreInfoClick={openMoreInfo} /> );
    }
  };

  return (
    <div className={styles.taskpaneContainer}>
      <div className={styles.mainContent}>
        {renderView()}

      </div>
      <div className={styles.footer}>
         <Link className={styles.footerLink} href="https://lexfania.ai/terminos-y-condiciones/" target="_blank">Términos de Uso</Link>
         <Text as="span" size={300}>|</Text>
         <Link className={styles.footerLink} href="https://lexfania.ai/aviso-de-privacidad/" target="_blank">Política de Privacidad</Link>
      </div>
    </div>
  );
};

export default App;
